import { useEffect, useState } from "react";
import { userRoles } from "@/lib/constants";
import client from "@/api/client";
import { PageHeader, Section } from "@/components/PageChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useUserNames } from "@/hooks/useUserNames";
import { toast } from "sonner";
import { apiError } from "@/api/client";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";

const EMPTY = { name: "", address: "", phone: "", gst: "", pan: "" };

export default function Companies() {
  const { user } = useAuth();
  const isAdmin = userRoles(user).includes("admin");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const { nameFor } = useUserNames();

  const load = async () => {
    const { data } = await client.get("/companies");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (r) => { setForm({ name: r.name, address: r.address || "", phone: r.phone || "", gst: r.gst || "", pan: r.pan || "" }); setEditId(r.id); setOpen(true); };

  const save = async () => {
    try {
      if (editId) await client.put(`/companies/${editId}`, form);
      else await client.post("/companies", form);
      toast.success(editId ? "Updated" : "Created");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this company?")) return;
    try { await client.delete(`/companies/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader
        title="Company Master"
        subtitle="Billing entities"
        actions={isAdmin && (
          <Button data-testid="company-new-btn" onClick={openNew} className="bg-slate-900 hover:bg-slate-800 rounded-sm">
            <Plus size={16} weight="bold" className="mr-1" /> New Company
          </Button>
        )}
      />
      <Section>
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left label-cap border-b border-slate-200 bg-slate-50">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">GST</th>
                <th className="px-4 py-3">PAN</th>
                <th className="px-4 py-3">Address</th>
                <th className="px-4 py-3">Entered By</th>
                {isAdmin && <th className="px-4 py-3 w-24">Actions</th>}
              </tr>
            </thead>
            <tbody data-testid="companies-tbody">
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 font-semibold">{r.name}</td>
                  <td className="px-4 py-2 font-mono">{r.phone}</td>
                  <td className="px-4 py-2 font-mono">{r.gst}</td>
                  <td className="px-4 py-2 font-mono">{r.pan}</td>
                  <td className="px-4 py-2 text-slate-600">{r.address}</td>
                  <td className="px-4 py-2 font-mono text-xs">{nameFor(r.created_by)}</td>
                  {isAdmin && (
                    <td className="px-4 py-2">
                      <div className="flex gap-1">
                        <button data-testid={`company-edit-${r.id}`} onClick={() => openEdit(r)} className="p-1.5 hover:bg-slate-100 rounded-sm"><PencilSimple size={14} /></button>
                        <button data-testid={`company-delete-${r.id}`} onClick={() => remove(r.id)} className="p-1.5 hover:bg-rose-50 text-rose-600 rounded-sm"><Trash size={14} /></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={isAdmin ? 7 : 6} className="px-4 py-10 text-center text-slate-500">No companies</td></tr>}
            </tbody>
          </table>
        </div>
      </Section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle>{editId ? "Edit" : "New"} Company</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div><label className="label-cap">Company Name</label><Input data-testid="company-name-input" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} /></div>
            <div><label className="label-cap">Address</label><Input data-testid="company-address-input" value={form.address} onChange={(e)=>setForm({...form, address: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label-cap">Phone</label><Input value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} /></div>
              <div><label className="label-cap">GST No.</label><Input value={form.gst} onChange={(e)=>setForm({...form, gst: e.target.value})} /></div>
            </div>
            <div><label className="label-cap">PAN</label><Input value={form.pan} onChange={(e)=>setForm({...form, pan: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="company-save-btn" onClick={save} className="bg-slate-900 hover:bg-slate-800 rounded-sm">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
